"use client";
import {
  __async
} from "../../../chunk-MMUBH76A.js";
import { jsx, jsxs } from "react/jsx-runtime";
import { AuthMethod, OAuthMethod } from "@getpara/web-sdk";
import { styled } from "styled-components";
import { useModalStore, useUserInfoStore } from "../../stores/index.js";
import { ModalStep } from "../../utils/steps.js";
import { openPopup } from "../../utils/openPopup.js";
import { useThemeStore } from "../../stores/theme/useThemeStore.js";
import { getTileButtonFlex } from "../../utils/getTileButtonFlex.js";
import { StyledCpslTileButton } from "../common.js";
import { brandedOAuthLogos, oAuthLogos } from "../../constants/oAuthLogos.js";
import { useEffect } from "react";
import { routeMobileExternalWallet } from "../../utils/routeMobileExternalWallet.js";
import { useGoBack } from "../../hooks/useGoBack.js";
import { useInternalClient } from "../../../provider/hooks/utils/useInternalClient.js";
import { useSetLoginURLs } from "../../hooks/useSetLoginURLs.js";
import { useUserAgent } from "@getpara/react-common";
const HAS_MORE_LENGTH = 3;
const OAuth = ({ methods }) => {
  const userAgent = useUserAgent();
  const goBack = useGoBack();
  const oAuthLogoVariant = useThemeStore((state) => state.oAuthLogoVariant);
  const isDark = useThemeStore((state) => state.isDark);
  const para = useInternalClient();
  const refs = useModalStore((state) => state.refs);
  const isPasskeySupported = useModalStore((state) => state.isPasskeySupported);
  const setFlow = useModalStore((state) => state.setFlow);
  const setStep = useModalStore((state) => state.setStep);
  const setAuthInfo = useUserInfoStore((state) => state.setAuthInfo);
  const setBiometricLocationHints = useModalStore((state) => state.setBiometricLocationHints);
  const setFarcasterConnectUri = useModalStore((state) => state.setFarcasterConnectUri);
  const farcasterConnectUri = useModalStore((state) => state.farcasterConnectUri);
  const showAll = useModalStore((state) => state.step === ModalStep.AUTH_MORE);
  const setIFrameUrl = useModalStore((state) => state.setIFrameUrl);
  const setIsIFrameReady = useModalStore((state) => state.setIsIFrameReady);
  const setAuthStepRoute = useModalStore((state) => state.setAuthStepRoute);
  const setWebAuthURLForCreate = useModalStore((state) => state.setWebAuthURLForCreate);
  const theme = useThemeStore((state) => state.theme);
  const setLoginURLs = useSetLoginURLs();
  useEffect(() => {
    const initializeFarcaster = () => __async(void 0, null, function* () {
      if (!methods.includes(OAuthMethod.FARCASTER)) {
        return;
      }
      const connectUri = yield para.getFarcasterConnectURL();
      setFarcasterConnectUri(connectUri);
    });
    initializeFarcaster();
  }, []);
  const hasMore = methods.length > HAS_MORE_LENGTH;
  const methodsToShow = showAll || !hasMore ? methods : methods.slice(0, HAS_MORE_LENGTH - 1);
  const handleShowAll = () => {
    setStep(ModalStep.AUTH_MORE);
  };
  const handleMethodClick = (method) => () => __async(void 0, null, function* () {
    var _a;
    switch (method) {
      case OAuthMethod.FARCASTER:
        if (!farcasterConnectUri) {
          return;
        }
        routeMobileExternalWallet(farcasterConnectUri);
        setStep(ModalStep.FARCASTER_OAUTH);
        break;
      case OAuthMethod.TELEGRAM:
        setStep(ModalStep.TELEGRAM_OAUTH);
        break;
      default:
        setStep(ModalStep.AWAITING_OAUTH);
        const oAuthURL = yield para.getOAuthURL({ method });
        refs.popupWindow.current = openPopup({
          url: oAuthURL,
          target: `${method}AuthPopup`,
          type: "OAUTH",
          current: refs.popupWindow.current
        });
        const { email, isError, userExists } = yield para.waitForOAuth({ popupWindow: (_a = refs.popupWindow.current) == null ? void 0 : _a.window });
        refs.popupWindow.current = void 0;
        if ((isError || !email) && refs.currentStep.current === ModalStep.AWAITING_OAUTH) {
          goBack();
          return;
        }
        setAuthInfo({ email });
        if (userExists) {
          const supportedAuthMethods = yield para.initiateUserLoginV2({ email });
          if (supportedAuthMethods.size > 0) {
            const biometricLocationHints = supportedAuthMethods.has(AuthMethod.PASSKEY) ? yield para.getUserBiometricLocationHints() : [];
            yield setLoginURLs({ supportedAuthMethods, authType: "email" });
            setFlow("login");
            setStep(ModalStep.BIOMETRIC_LOGIN);
            setBiometricLocationHints(biometricLocationHints);
            return;
          }
        }
        const supportedCreateAuthMethods = yield para.getSupportedCreateAuthMethods();
        setIsIFrameReady(false);
        setFlow("signUp");
        const supportsPasskey = supportedCreateAuthMethods.has(AuthMethod.PASSKEY);
        const supportsPassword = supportedCreateAuthMethods.has(AuthMethod.PASSWORD);
        const isMobileNoPasskey = !isPasskeySupported && (userAgent == null ? void 0 : userAgent.device.type) === "mobile";
        if (supportsPasskey) {
          setWebAuthURLForCreate(yield para.shortenLoginLink(yield para.getSetUpBiometricsURL({ authType: "email" })));
          if (!supportsPassword && !isMobileNoPasskey) {
            setStep(ModalStep.BIOMETRIC_CREATION);
            return;
          }
        }
        if (supportsPassword || isMobileNoPasskey) {
          setIFrameUrl(yield para.shortenLoginLink(yield para.getSetupPasswordURL({ authType: "email", theme })));
        }
        setAuthStepRoute(supportsPasskey && !isMobileNoPasskey ? ModalStep.BIOMETRIC_CREATION : ModalStep.PASSWORD_CREATION);
        break;
    }
  });
  const useBrandedLogos = oAuthLogoVariant === "default";
  const useDarkLogos = useBrandedLogos ? isDark : oAuthLogoVariant !== "dark";
  const showMoreButton = !showAll && hasMore;
  return /* @__PURE__ */ jsxs(OAuthContainer, { children: [
    methodsToShow.map((method, index) => /* @__PURE__ */ jsx(
      OAuthButton,
      {
        $isDark: useDarkLogos,
        icon: useBrandedLogos ? brandedOAuthLogos[method] : oAuthLogos[method],
        onClick: handleMethodClick(method),
        $index: index,
        $totalItems: showMoreButton ? HAS_MORE_LENGTH : methodsToShow.length
      },
      method
    )),
    showMoreButton && /* @__PURE__ */ jsx(
      OAuthButton,
      {
        $isDark: useDarkLogos,
        icon: "moreLoginOptions",
        onClick: handleShowAll,
        $index: HAS_MORE_LENGTH - 1,
        $totalItems: HAS_MORE_LENGTH
      }
    )
  ] });
};
const OAuthContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
`;
const OAuthButton = styled(StyledCpslTileButton)`
  flex: ${({ $index, $totalItems }) => getTileButtonFlex($index, $totalItems)};

  --button-icon-color: ${({ $isDark }) => $isDark ? "white" : "black"};
`;
export {
  OAuth
};
